import Alpine, { Alpine as AlpineType, AlpineComponent } from "alpinejs";

export const CHUNK_DISTRIBUTION_5 = [
    2, // [0, 0, 1, 0, 0]
    1, // [0, 1, 0, 0, 0]
    3, // [0, 0, 0, 1, 0]
    2, // [0, 0, 1, 0, 0]
    0, // [1, 0, 0, 0, 0]
    4, // [0, 0, 0, 0, 1]
    2, // [0, 0, 1, 0, 0]
    1, // [0, 1, 0, 0, 0]
    3, // [0, 0, 0, 1, 0]
    1, // [0, 1, 0, 0, 0]
    3, // [0, 0, 0, 1, 0]
    0, // [1, 0, 0, 0, 0]
    4, // [0, 0, 0, 0, 1]
    2, // [0, 0, 1, 0, 0]
];

export const CHUNK_DISTRIBUTION_3 = [
    2, // [0, 1, 0]
    1, // [1, 0, 0]
    3, // [0, 0, 1]
    2, // [0, 1, 0]
    1, // [1, 0, 0]
    3, // [0, 0, 1]
    2, // [0, 1, 0]
    2, // [0, 1, 0]
    1, // [1, 0, 0]
    3, // [0, 0, 1]
    2, // [0, 1, 0]
];

declare global {
    const Alpine: AlpineType;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.Alpine = Alpine;

Alpine.data(
    "verticalScroller",
    (): AlpineComponent => ({
        filterOpen: false,
        filterString: "",
        filterStringText: "",
        columns: [],
        domColumns: [],
        domSource: [],
        sourceReferencesRefs: [],
        sourceClonesRefs: [],
        sourceReferences: [],
        sourceClones: [],
        filters: [],
        filterItem(element) {
            return (
                !this.filterString ||
                this.filters.some((attribute) => element.verticalScroller[attribute]?.includes(this.filterString))
            );
        },
        filterDomSource() {
            this.resetDomSource();
            this.sourceReferences.forEach((item) => {
                item._matchFilter = this.filterItem(item);
                item.classList.toggle("opacity-30", !item._matchFilter);
                item.classList.toggle("bluroverlay", !item._matchFilter);
                item.classList.toggle("pointer-events-none", !item._matchFilter);
            });
            this.sourceReferences.sort((a, b) => {
                return a._matchFilter && b._matchFilter ? 0 : a._matchFilter ? -1 : 1;
            });
            this.sourceClones.forEach((item) => {
                item.classList.toggle("opacity-30", this.filterString);
                item.classList.toggle("bluroverlay", this.filterString);
                item.classList.toggle("pointer-events-none", this.filterString);
            });
            this.sourceClones.sort((a, b) => {
                return a._matchFilter && b._matchFilter ? 0 : a._matchFilter ? -1 : 1;
            });
        },
        resetDomSource() {
            this.sourceReferences = [...this.sourceReferencesRefs];
            this.sourceClones = [...this.sourceClonesRefs];
        },
        get isBreakpoint1024() {
            return document.documentElement.clientWidth >= 1024;
        },
        get isBreakpoint801() {
            return document.documentElement.clientWidth >= 801;
        },
        get isBreakpoint375() {
            return document.documentElement.clientWidth >= 375;
        },
        init() {
            this.domSource = [...this.$refs.datasource.children];
            this.filters = JSON.parse(this.$el.dataset.filters);

            // desktop..
            this.columns = [
                { id: "a", scroll: -50 },
                { id: "b", scroll: -25 },
                { id: "c", scroll: 0 },
                { id: "d", scroll: -25 },
                { id: "e", scroll: -50 },
            ];

            this.$watch("currentScroll", (value) => {
                this.columns = this.columns.map((column, i) => {
                    column.scroll = Math.abs(i - 2) * -25 + Math.abs(i - 2) * value * 0.3;
                    return column;
                });
            });

            window.addEventListener("scroll", () => {
                this.handleScroll();
            });

            this.$nextTick(() => {
                this.domColumns = [...this.$refs.gridContainer.children].filter(
                    (element) => element.tagName !== "TEMPLATE"
                );
                this.domSource.forEach((source: HTMLElement & { verticalScroller: unknown }, index) => {
                    const verticalScroller = {
                        index,
                        ...Object.fromEntries(
                            Object.entries(source.dataset).map(([key, value]) => [
                                key,
                                value.includes("[") ? JSON.parse(value) : value,
                            ])
                        ),
                    };
                    source.verticalScroller = verticalScroller;
                    this.sourceReferencesRefs.push(source);
                    const copy = this.createCopy(source);
                    copy.verticalScroller = verticalScroller;
                    this.sourceClonesRefs.push(copy);
                });
                this.domSource.slice(0, 14).forEach((source) => {
                    const copy = this.createCopy(source);
                    copy.verticalScroller = source.verticalScroller;
                    copy.verticalScroller.index += this.domSource.length;
                    this.sourceClonesRefs.push(copy);
                });
                this.resetDomSource();
                this.distributeItems();
            });
        },
        distributeItems() {
            this.sourceClonesRefs.forEach((element) => element.remove());
            const pureItemsCount = this.sourceReferencesRefs.length;
            for (let index = 0; index < pureItemsCount; index++) {
                const { index: position, overflow } = this.getPosition(index);
                const element = overflow ? this.sourceClones.pop() : this.sourceReferences.shift();
                this.domColumns[position].appendChild(element);
            }
            // remaining items (padded with clones to fill loop)
            const leftoverCount =
                this.sourceReferences.length +
                Math.abs(this.getItemLoopCount() - this.sourceReferences.length - this.getItemLoopCount());
            for (let index = 0; index < leftoverCount; index++) {
                const { index: position, overflow } = this.getPosition(pureItemsCount + index);
                const element = overflow
                    ? this.sourceClones.shift()
                    : this.sourceReferences.shift() ?? this.sourceClones.shift();
                this.domColumns[position].appendChild(element);
            }
        },
        filter() {
            this.filterDomSource();
            this.distributeItems();
            window.scrollTo({ top: 0, behavior: "smooth" });
        },
        getPosition(itemIndex) {
            if (this.isBreakpoint1024) {
                const index = CHUNK_DISTRIBUTION_5[itemIndex % this.getItemLoopCount()];
                return {
                    index,
                    overflow: [0, 4].includes(index),
                };
            } else if (this.isBreakpoint801) {
                return {
                    index: CHUNK_DISTRIBUTION_3[itemIndex % this.getItemLoopCount()],
                    overflow: false,
                };
            } else if (this.isBreakpoint375) {
                const index = CHUNK_DISTRIBUTION_3[itemIndex % this.getItemLoopCount()];
                return {
                    index,
                    overflow: [1, 3].includes(index),
                };
            } else {
                return {
                    index: 2,
                    overflow: false,
                };
            }
        },
        getItemLoopCount() {
            if (this.isBreakpoint1024) {
                return 14;
            } else if (this.isBreakpoint375) {
                return 10;
            } else {
                return 0;
            }
        },
        createCopy(element) {
            const copy = element.cloneNode(true);
            copy.isClone = true;
            return copy;
        },
        resized() {
            this.resetDomSource();
            this.distributeItems();
        },
        filterControl: {
            ["x-model"]: "filterString",
            ["x-on:change"]: "filter",
        },
        filterbutton: {
            "x-transition:enter.delay.1s": "",
        },
        filterdiv: {
            "x-transition:enter": "filterback duration-1000",
            "x-transition:leave": "filterinitial duration-1000",
        },
        gridContainer: {
            ["x-on:resize.throttle.window"]: "resized",
        },
        currentScroll: 0,
        handleScroll: function () {
            this.currentScroll = document.documentElement.scrollTop;
        },
    })
);
